// VibeScore Service - Advanced Sentiment Analysis for Content Energy Frequency
import vibeLexicon from '../data/vibeLexicon.json';

interface VibeScoreBreakdown {
  language: string;
  positiveScore: number;
  negativeScore: number;
  culturalMultiplier: number;
  isSarcastic: boolean;
  bonuses: {
    hasQuestion: boolean;
    hasSpecificNumbers: boolean;
    isEducational: boolean;
  };
  wordMatches: {
    positive: Array<{
      word: string;
      weight: number;
      frequency: number;
      contribution: number;
    }>;
    negative: Array<{
      word: string;
      weight: number;
      frequency: number;
      contribution: number;
    }>;
  };
  error?: string;
}

interface ChakraColor {
  color: string;
  name: string;
  description: string;
}

export interface VibeScoreResult {
  score: number; // 0-100
  language: string;
  chakraColor: ChakraColor;
  breakdown: VibeScoreBreakdown;
}

/**
 * Detect language of text based on Dutch-specific words and patterns
 */
function detectLanguage(text: string): 'nl' | 'en' {
  const dutchIndicators = [
    'van', 'de', 'het', 'een', 'en', 'is', 'dat', 'zijn', 'met', 'voor',
    'op', 'door', 'als', 'maar', 'kan', 'heeft', 'werd', 'omdat', 'zou',
    'ondernemer', 'samenwerking', 'groei', 'dankbaar', 'waarderen'
  ];
  
  const words = text.toLowerCase().split(/\s+/);
  const dutchMatches = words.filter(word => dutchIndicators.includes(word)).length;
  const dutchRatio = dutchMatches / words.length;
  
  return dutchRatio > 0.15 ? 'nl' : 'en';
}

/**
 * Check for sarcasm indicators in text
 */
function detectSarcasm(text: string, language: 'en' | 'nl'): boolean {
  const sarcasmWords = vibeLexicon.sarcasm_indicators[language] || [];
  const punctuationSarcasm = vibeLexicon.punctuation_sarcasm;
  
  // Check for sarcastic punctuation
  const hasSarcasmPunctuation = punctuationSarcasm.some(punct => text.includes(punct));
  
  // Check for positive words in negative context
  const words = text.toLowerCase().split(/\s+/);
  const positiveWords = Object.keys(vibeLexicon.positive[language] || {});
  const negativeWords = Object.keys(vibeLexicon.negative[language] || {});
  
  let positiveCount = 0;
  let negativeCount = 0;
  
  words.forEach(word => {
    if (positiveWords.includes(word)) positiveCount++;
    if (negativeWords.includes(word)) negativeCount++;
  });
  
  // Sarcasm detection: positive words + negative context + punctuation
  const mixedSentiment = positiveCount > 0 && negativeCount > 0;
  const hasSarcasmWords = words.some(word => sarcasmWords.includes(word));
  
  return hasSarcasmPunctuation || (mixedSentiment && hasSarcasmWords);
}

/**
 * Calculate word frequency adjustment (diminishing returns for repeated words)
 */
function getFrequencyAdjustment(frequency: number): number {
  if (frequency === 1) return 1.0;
  if (frequency === 2) return 1.2;
  if (frequency === 3) return 1.3;
  return 1.3 + Math.log(frequency - 2) * 0.1; // Logarithmic scaling
}

/**
 * Calculate VibeScore for a post
 */
export function calculateVibeScore(text: string): VibeScoreResult {
  if (!text || text.trim().length === 0) {
    return { 
      score: 50, 
      language: 'nl',
      chakraColor: getChakraColor(50),
    breakdown: { 
      language: 'nl',
      positiveScore: 0,
      negativeScore: 0,
      culturalMultiplier: 1,
      isSarcastic: false,
      bonuses: { hasQuestion: false, hasSpecificNumbers: false, isEducational: false },
      wordMatches: { positive: [], negative: [] },
      error: 'Empty text' 
    }
    };
  }

  const language = detectLanguage(text);
  const words = text.toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Remove punctuation but keep emojis for sarcasm detection
    .split(/\s+/)
    .filter(word => word.length > 1);

  const positiveWords = vibeLexicon.positive[language] || {};
  const negativeWords = vibeLexicon.negative[language] || {};
  
  // Count word frequencies
  const wordFreq: Record<string, number> = {};
  words.forEach(word => {
    wordFreq[word] = (wordFreq[word] || 0) + 1;
  });

  // Calculate positive score
  let positiveScore = 0;
  let positiveMatches: Array<{ word: string; weight: number; frequency: number; contribution: number }> = [];
  Object.entries(wordFreq).forEach(([word, frequency]) => {
    if (positiveWords[word as keyof typeof positiveWords]) {
      const weight = positiveWords[word as keyof typeof positiveWords];
      const adjustment = getFrequencyAdjustment(frequency);
      const contribution = weight * adjustment;
      positiveScore += contribution;
      positiveMatches.push({ word, weight, frequency, contribution });
    }
  });

  // Calculate negative score
  let negativeScore = 0;
  let negativeMatches: Array<{ word: string; weight: number; frequency: number; contribution: number }> = [];
  Object.entries(wordFreq).forEach(([word, frequency]) => {
    if (negativeWords[word as keyof typeof negativeWords]) {
      const weight = negativeWords[word as keyof typeof negativeWords];
      const intensityPenalty = frequency > 1 ? 1.3 : 1.0; // Penalty for repeated negative words
      const contribution = Math.abs(weight) * intensityPenalty;
      negativeScore += contribution;
      negativeMatches.push({ word, weight, frequency, contribution });
    }
  });

  // Detect sarcasm
  const isSarcastic = detectSarcasm(text, language);
  
  // Apply cultural multipliers
  const culturalMultiplier = language === 'nl' 
    ? 1 + vibeLexicon.cultural_multipliers.nl.directness_bonus
    : vibeLexicon.cultural_multipliers.en.politeness_baseline;

  // Base calculation
  let baseScore = 50 + (positiveScore - negativeScore) * 20; // Scale to 0-100 range
  
  // Apply cultural adjustment
  baseScore *= culturalMultiplier;
  
  // Apply penalties
  if (isSarcastic) {
    baseScore *= 0.3; // Heavy sarcasm penalty
  }
  
  // Context bonuses
  const hasQuestion = text.includes('?');
  const hasSpecificNumbers = /\d+%|\d+[kmb]|\$\d+|€\d+/.test(text);
  const isEducational = /learn|lesson|insight|tip|advice|strategy|leren|les|inzicht|tip|advies|strategie/.test(text.toLowerCase());
  
  if (hasQuestion) baseScore += 5; // Community engagement
  if (hasSpecificNumbers) baseScore += 8; // Concrete data
  if (isEducational) baseScore += 10; // Educational value
  
  // Clamp to 0-100 range
  const finalScore = Math.max(0, Math.min(100, Math.round(baseScore)));
  
  // Determine chakra color
  const chakraColor = getChakraColor(finalScore);
  
  return {
    score: finalScore,
    language,
    chakraColor,
    breakdown: {
      language,
      positiveScore: Math.round(positiveScore * 100) / 100,
      negativeScore: Math.round(negativeScore * 100) / 100,
      culturalMultiplier,
      isSarcastic,
      bonuses: {
        hasQuestion,
        hasSpecificNumbers,
        isEducational
      },
      wordMatches: {
        positive: positiveMatches,
        negative: negativeMatches
      }
    }
  };
}

/**
 * Get chakra color based on score
 */
function getChakraColor(score: number): ChakraColor {
  if (score >= 90) return { color: '#8E24AA', name: 'violet', description: 'Wijs, voedzaam' };
  if (score >= 75) return { color: '#3949AB', name: 'indigo', description: 'Inzicht' };
  if (score >= 60) return { color: '#1E88E5', name: 'blue', description: 'Heldere communicatie' };
  if (score >= 45) return { color: '#43A047', name: 'green', description: 'Hart, verbinding' };
  if (score >= 30) return { color: '#FDD835', name: 'yellow', description: 'Drive, richting' };
  if (score >= 15) return { color: '#FB8C00', name: 'orange', description: 'Creatie, maar onrustig' };
  return { color: '#E53935', name: 'red', description: 'Root, mogelijk triggerend' };
}

/**
 * Batch process multiple posts
 */
export function batchCalculateVibeScores(posts: Array<{ text: string; [key: string]: any }>): Array<{ [key: string]: any; vibeScore: VibeScoreResult }> {
  return posts.map(post => ({
    ...post,
    vibeScore: calculateVibeScore(post.text)
  }));
}

/**
 * Get ranking weight for feed algorithm (with visibility boosts)
 */
export function getFeedRankingWeight(
  vibeScore: number, 
  engagementMetrics: Record<string, number> = {}, 
  freshnessDecay: number = 1.0,
  visibilityBoosts?: { newsfeed?: { until: Date; weight: number }; jumpstarter?: { until: Date; weight: number } }
): number {
  const normalizedVibeScore = vibeScore / 100;
  const qualitySignals = (
    (engagementMetrics.saves || 0) * 0.4 +
    (engagementMetrics.shares || 0) * 0.3 +
    (engagementMetrics.comments || 0) * 0.2 +
    (engagementMetrics.likes || 0) * 0.1
  ) / 100; // Normalize to 0-1 range
  
  const networkAffinity = engagementMetrics.networkAffinity || 0.5; // Default neutral
  
  const baseWeight = (
    0.55 * normalizedVibeScore +
    0.25 * Math.min(qualitySignals, 1.0) +
    0.10 * freshnessDecay +
    0.10 * networkAffinity
  );

  // Apply visibility boosts
  let boostWeight = 0;
  const now = new Date();
  
  if (visibilityBoosts?.newsfeed) {
    const boost = visibilityBoosts.newsfeed;
    if (boost.until > now) {
      boostWeight += boost.weight; // +0.15 for newsfeed boost
    }
  }
  
  if (visibilityBoosts?.jumpstarter) {
    const boost = visibilityBoosts.jumpstarter;
    if (boost.until > now) {
      boostWeight += boost.weight; // +0.2 for jumpstarter boost
    }
  }
  
  return baseWeight + boostWeight;
}

export class VibeScoreService {
  /**
   * Analyze text content and return VibeScore (legacy method for compatibility)
   */
  static analyzeContent(content: string, language: 'en' | 'nl' = 'nl'): VibeScoreResult {
    return calculateVibeScore(content);
  }

  /**
   * Generate chakra meter colors for visualization
   */
  static getChakraColors(): string[] {
    return [
      '#dc2626', // red (root chakra)
      '#ea580c', // orange (sacral chakra)
      '#d97706', // amber (solar plexus)
      '#16a34a', // green (heart chakra)
      '#0ea5e9', // sky blue (throat chakra)
      '#7c3aed', // violet (third eye)
      '#9333ea'  // purple (crown chakra)
    ];
  }
}
